import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Banknote, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { eur, apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear, lineId } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shippingMethods, setShippingMethods] = useState([]);
  const [shipId, setShipId] = useState("");
  const [payment, setPayment] = useState("card");
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [threshold, setThreshold] = useState(80);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "Ελλάδα", notes: "" });

  useEffect(() => {
    if (items.length === 0) navigate("/shop");
    api.get("/shipping-methods").then(({ data }) => { setShippingMethods(data); if (data[0]) setShipId(data[0].id); }).catch(() => {});
    api.get("/settings").then(({ data }) => setThreshold(data.free_shipping_threshold || 80)).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { if (user) setForm((f) => ({ ...f, email: user.email || "", first_name: user.name?.split(" ")[0] || "", last_name: user.name?.split(" ").slice(1).join(" ") || "", phone: user.phone || "" })); }, [user]);

  const ship = shippingMethods.find((s) => s.id === shipId);
  const applyCoupon = async () => {
    try { const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}&subtotal=${subtotal}`); setCoupon(data); toast.success("Ο κωδικός εφαρμόστηκε!"); }
    catch (e) { setCoupon(null); toast.error(apiErr(e)); }
  };

  const discount = coupon ? (coupon.type === "percentage" ? subtotal * coupon.value / 100 : coupon.type === "fixed" ? Math.min(coupon.value, subtotal) : 0) : 0;
  let shipping = ship ? ship.price + (payment === "cod" ? (ship.cod_fee || 0) : 0) : 0;
  const freeShip = coupon?.type === "free_shipping" || subtotal - discount >= threshold;
  if (freeShip) shipping = payment === "cod" && ship ? (ship.cod_fee || 0) : 0;
  const total = subtotal - discount + shipping;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.first_name && form.last_name && form.email.includes("@") && form.phone && form.address && form.city && form.postal_code && shipId;

  const placeOrder = async () => {
    if (!valid) { toast.error("Συμπλήρωσε όλα τα υποχρεωτικά πεδία"); return; }
    setSubmitting(true);
    try {
      const orderItems = items.map((i) => ({ product_id: i.product_id, color: i.color, size: i.size, quantity: i.quantity }));
      const { data: order } = await api.post("/orders", {
        items: orderItems,
        customer: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, address: form.address, city: form.city, postal_code: form.postal_code, country: form.country },
        shipping_method_id: shipId, payment_method: payment, coupon_code: coupon?.code || null, notes: form.notes,
      });
      if (payment === "cod") {
        clear();
        toast.success("Η παραγγελία καταχωρήθηκε με επιτυχία!");
        navigate(`/tracking/${order.order_number}`);
      } else {
        const { data: co } = await api.post("/checkout/stripe", { order_number: order.order_number, origin_url: window.location.origin });
        localStorage.setItem("skg_pending_order", order.order_number);
        window.location.href = co.checkout_url;
      }
    } catch (e) { toast.error(apiErr(e)); setSubmitting(false); }
  };

  const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-3 text-sm outline-none focus:border-[#FF2E4C] transition-colors";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-8">Ολοκλήρωση Παραγγελίας</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <h2 className="font-display font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><span className="w-6 h-6 bg-[#FF2E4C] rounded-full text-xs flex items-center justify-center">1</span> Στοιχεία Επικοινωνίας</h2>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Όνομα *" value={form.first_name} onChange={set("first_name")} className={inp} data-testid="checkout-first-name" />
              <input placeholder="Επώνυμο *" value={form.last_name} onChange={set("last_name")} className={inp} data-testid="checkout-last-name" />
              <input placeholder="Email *" value={form.email} onChange={set("email")} className={`${inp} col-span-2`} data-testid="checkout-email" />
              <input placeholder="Τηλέφωνο *" value={form.phone} onChange={set("phone")} className={`${inp} col-span-2`} data-testid="checkout-phone" />
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><span className="w-6 h-6 bg-[#FF2E4C] rounded-full text-xs flex items-center justify-center">2</span> Διεύθυνση Αποστολής</h2>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Διεύθυνση *" value={form.address} onChange={set("address")} className={`${inp} col-span-2`} data-testid="checkout-address" />
              <input placeholder="Πόλη *" value={form.city} onChange={set("city")} className={inp} data-testid="checkout-city" />
              <input placeholder="Τ.Κ. *" value={form.postal_code} onChange={set("postal_code")} className={inp} data-testid="checkout-postal" />
              <input placeholder="Χώρα" value={form.country} onChange={set("country")} className={`${inp} col-span-2`} />
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><span className="w-6 h-6 bg-[#FF2E4C] rounded-full text-xs flex items-center justify-center">3</span> Μέθοδος Αποστολής</h2>
            <div className="space-y-2">
              {shippingMethods.map((s) => (
                <label key={s.id} className={`flex items-center justify-between border rounded-lg p-4 cursor-pointer transition-colors ${shipId === s.id ? "border-[#FF2E4C] bg-[#FF2E4C]/5" : "border-zinc-700"}`} data-testid={`checkout-shipping-${s.type}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={shipId === s.id} onChange={() => setShipId(s.id)} className="accent-[#FF2E4C]" />
                    <div><p className="text-sm font-semibold">{s.name}</p><p className="text-xs text-zinc-500">{s.greek_name}</p></div>
                  </div>
                  <span className="text-sm font-semibold">{eur(s.price)}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><span className="w-6 h-6 bg-[#FF2E4C] rounded-full text-xs flex items-center justify-center">4</span> Πληρωμή</h2>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer ${payment === "card" ? "border-[#FF2E4C] bg-[#FF2E4C]/5" : "border-zinc-700"}`} data-testid="checkout-payment-card">
                <input type="radio" checked={payment === "card"} onChange={() => setPayment("card")} className="accent-[#FF2E4C]" />
                <CreditCard className="w-5 h-5" /><div><p className="text-sm font-semibold">Κάρτα (Credit / Debit)</p><p className="text-xs text-zinc-500">Ασφαλής πληρωμή μέσω Stripe · Apple Pay / Google Pay</p></div>
              </label>
              <label className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer ${payment === "cod" ? "border-[#FF2E4C] bg-[#FF2E4C]/5" : "border-zinc-700"}`} data-testid="checkout-payment-cod">
                <input type="radio" checked={payment === "cod"} onChange={() => setPayment("cod")} className="accent-[#FF2E4C]" />
                <Banknote className="w-5 h-5" /><div><p className="text-sm font-semibold">Αντικαταβολή</p><p className="text-xs text-zinc-500">Πληρωμή κατά την παράδοση{ship?.cod_fee ? ` (+${eur(ship.cod_fee)})` : ""}</p></div>
              </label>
            </div>
            <textarea placeholder="Σημειώσεις παραγγελίας (προαιρετικό)" value={form.notes} onChange={set("notes")} rows={2} className={`${inp} mt-4`} data-testid="checkout-notes" />
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card-surface rounded-lg p-6 sticky top-28">
            <h2 className="font-display font-bold uppercase tracking-tight mb-4">Η Παραγγελία σου</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {items.map((it) => (
                <div key={lineId(it)} className="flex gap-3">
                  <div className="relative"><img src={it.image} alt="" className="w-14 h-16 object-cover rounded" /><span className="absolute -top-1.5 -right-1.5 bg-zinc-700 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{it.quantity}</span></div>
                  <div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{it.name}</p><p className="text-[10px] font-mono text-zinc-500 uppercase">{it.color}/{it.size}</p></div>
                  <span className="text-xs font-semibold">{eur(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Κωδικός" className="flex-1 bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-2 text-sm outline-none uppercase" data-testid="checkout-coupon-input" />
              <button onClick={applyCoupon} className="px-4 bg-white text-black text-xs font-bold uppercase rounded" data-testid="checkout-coupon-apply">ΕΦΑΡΜΟΓΗ</button>
            </div>
            <div className="space-y-2 text-sm border-t border-zinc-800 pt-4">
              <div className="flex justify-between"><span className="text-zinc-400">Υποσύνολο</span><span>{eur(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#FF2E4C]"><span>Έκπτωση</span><span>-{eur(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-zinc-400">Μεταφορικά</span><span>{shipping === 0 ? "ΔΩΡΕΑΝ" : eur(shipping)}</span></div>
              <div className="flex justify-between font-display font-black text-lg border-t border-zinc-800 pt-3 mt-2"><span>ΣΥΝΟΛΟ</span><span data-testid="checkout-total">{eur(total)}</span></div>
            </div>
            <button onClick={placeOrder} disabled={submitting || !valid} className="btn-red w-full mt-5 py-4 text-sm font-bold tracking-widest uppercase rounded flex items-center justify-center gap-2 disabled:opacity-40" data-testid="checkout-form-submit-button">
              <Lock className="w-4 h-4" /> {submitting ? "ΕΠΕΞΕΡΓΑΣΙΑ..." : payment === "cod" ? "ΚΑΤΑΧΩΡΗΣΗ ΠΑΡΑΓΓΕΛΙΑΣ" : `ΠΛΗΡΩΜΗ ${eur(total)}`}
            </button>
            <p className="text-[10px] text-zinc-500 text-center mt-3 font-mono uppercase">🔒 Ασφαλής κρυπτογραφημένη πληρωμή</p>
          </div>
        </div>
      </div>
    </div>
  );
}
